import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { theme } from 'antd';
import type { ApexOptions } from 'apexcharts';

import {
  OrganizationStorage,
  STORAGE_AREAS,
} from '@totem/components/administration/storageReporting/types';
import { formatStorage } from '@totem/components/administration/storageReporting/utilities';
import { isDarkBackground } from '@totem/components/software/vulnerabilityReport/summary/chartColors';

type Props = {
  organizations: OrganizationStorage[];
};

const TOP_ORGANIZATIONS = 10;

// Stacked bar of the largest organizations by storage area
const StorageReportChart = ({ organizations }: Props) => {
  const { token } = theme.useToken();
  const isDark = isDarkBackground(token.colorBgContainer);

  const top = organizations
    .filter((organization) => organization.totalBytes > 0)
    .slice(0, TOP_ORGANIZATIONS);

  if (top.length === 0) {
    return null;
  }

  const options: ApexOptions = {
    chart: {
      type: 'bar',
      stacked: true,
      toolbar: { show: false },
      animations: { enabled: false },
      background: 'transparent',
      fontFamily: 'inherit',
    },
    theme: { mode: isDark ? 'dark' : 'light' },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '60%',
        borderRadius: 3,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
      },
    },
    stroke: { width: 1, colors: [token.colorBgContainer] },
    colors: STORAGE_AREAS.map((area) => area.color),
    grid: { borderColor: token.colorBorderSecondary },
    dataLabels: { enabled: false },
    legend: {
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '12px',
      labels: { colors: token.colorTextSecondary },
    },
    xaxis: {
      categories: top.map((organization) => organization.name),
      labels: {
        style: { colors: token.colorTextSecondary },
        formatter: (value: string) => formatStorage(Number(value)),
      },
    },
    yaxis: {
      labels: {
        maxWidth: 200,
        style: { colors: token.colorTextSecondary },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      theme: isDark ? 'dark' : 'light',
      y: { formatter: (value: number) => formatStorage(value) },
    },
  };

  return (
    <ReactApexChart
      type="bar"
      height={top.length * 32 + 90}
      options={options}
      series={STORAGE_AREAS.map((area) => ({
        name: area.label,
        data: top.map((organization) => organization.storage[area.key].bytes),
      }))}
    />
  );
};

export default StorageReportChart;
